import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, Trash2, EyeOff, Eye } from "lucide-react";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { PageLoader, useToast, useConfirm } from "@/components/ui";
import { Field, TextInput, Select } from "@/components/fields";
import type { Lookup } from "@shared/types";

const CATEGORY_LABELS: Record<string, string> = {
  status: "Activity statuses",
  priority: "Priorities",
  phase: "Standard project phases",
  responsible_role: "Responsible roles",
  project_status: "Project statuses",
};
const TREAT_AS = [
  { value: "", label: "Normal (in progress)" },
  { value: "not_started", label: "Counts as: not started" },
  { value: "done", label: "Counts as: complete" },
  { value: "cancelled", label: "Counts as: cancelled" },
];

export function AdminLookups() {
  const qc = useQueryClient();
  const toast = useToast();
  const { confirm, node } = useConfirm();
  const [cat, setCat] = useState("status");
  const { data, isLoading } = useQuery({ queryKey: ["lookups"], queryFn: () => api.get<{ lookups: Record<string, Lookup[]> }>("/lookups") });
  const items = (data?.lookups[cat] ?? []).slice().sort((a, b) => a.seq - b.seq);
  const invalidate = () => qc.invalidateQueries({ queryKey: ["lookups"] });

  const add = useMutation({ mutationFn: (b: any) => api.post("/lookups", b), onSuccess: () => { invalidate(); toast.push("success", "Added"); } });
  const upd = useMutation({ mutationFn: ({ id, ...b }: any) => api.put(`/lookups/${id}`, b), onSuccess: invalidate });
  const del = useMutation({
    mutationFn: (id: string) => api.del(`/lookups/${id}`),
    onSuccess: () => { invalidate(); toast.push("success", "Deleted"); },
    onError: (e: any) => toast.push("error", e?.message || "Cannot delete"),
  });

  const [nf, setNf] = useState({ label: "", color: "#3f7d5c", treatAs: "" });

  if (isLoading) return <PageLoader />;

  return (
    <>
      <PageHeader title="Dropdown Lists" subtitle="Every dropdown in the schedule is editable here — add options, recolour them, retire old ones." />

      <div className="mb-4 flex flex-wrap gap-1.5">
        {Object.keys(CATEGORY_LABELS).map((c) => (
          <button key={c} onClick={() => setCat(c)}
            className={`rounded-lg px-3 py-1.5 text-sm ${cat === c ? "bg-brand-700 text-white" : "text-ink-soft hover:bg-surface-2"}`}>
            {CATEGORY_LABELS[c]}
          </button>
        ))}
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <div className="card p-4 lg:col-span-2">
          <table className="w-full text-sm">
            <thead><tr>{["Order", "Label", "Colour", cat === "status" ? "Behaviour" : "", "Active", ""].map((h, i) => <th key={i} className="th">{h}</th>)}</tr></thead>
            <tbody>
              {items.map((l) => (
                <tr key={l.id} className="border-b border-line">
                  <td className="td text-ink-faint">{l.seq}</td>
                  <td className="td">
                    <TextInput defaultValue={l.label} className="!py-1"
                      onBlur={(e) => e.target.value !== l.label && upd.mutate({ id: l.id, label: e.target.value })} />
                    <span className="text-[11px] text-ink-faint">value: {l.value}</span>
                  </td>
                  <td className="td">
                    <input type="color" defaultValue={l.color || "#888888"} className="h-8 w-12 rounded border border-line"
                      onBlur={(e) => e.target.value !== l.color && upd.mutate({ id: l.id, color: e.target.value })} />
                  </td>
                  <td className="td">
                    {cat === "status" && (
                      <Select defaultValue={(l.meta as any)?.treatAs || ""} className="!py-1"
                        onChange={(e) => upd.mutate({ id: l.id, meta: e.target.value ? { treatAs: e.target.value } : {} })}>
                        {TREAT_AS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </Select>
                    )}
                  </td>
                  <td className="td">
                    <button className="btn-ghost !p-1" onClick={() => upd.mutate({ id: l.id, isActive: !l.isActive })}>
                      {l.isActive ? <Eye size={15} /> : <EyeOff size={15} className="text-ink-faint" />}
                    </button>
                  </td>
                  <td className="td">
                    {!l.isSystem && (
                      <button className="btn-ghost !p-1 text-ink-faint hover:text-st-delayed"
                        onClick={async () => { if (await confirm("Delete option", `Delete "${l.label}"?`, true)) del.mutate(l.id); }}>
                        <Trash2 size={14} />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="card h-fit p-4">
          <h3 className="mb-3 text-sm font-semibold">Add option</h3>
          <div className="space-y-3">
            <Field label="Label"><TextInput value={nf.label} onChange={(e) => setNf({ ...nf, label: e.target.value })} /></Field>
            <Field label="Colour"><input type="color" value={nf.color} onChange={(e) => setNf({ ...nf, color: e.target.value })} className="h-9 w-full rounded border border-line" /></Field>
            {cat === "status" && (
              <Field label="Behaviour"><Select value={nf.treatAs} onChange={(e) => setNf({ ...nf, treatAs: e.target.value })}>
                {TREAT_AS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </Select></Field>
            )}
            <button className="btn-primary w-full" disabled={!nf.label}
              onClick={() => { add.mutate({ category: cat, label: nf.label, color: nf.color, meta: nf.treatAs ? { treatAs: nf.treatAs } : undefined }); setNf({ label: "", color: "#3f7d5c", treatAs: "" }); }}>
              <Plus size={15} /> Add
            </button>
          </div>
        </div>
      </div>
      {node}
    </>
  );
}
